import { CloseOutlined } from '@ant-design/icons';
import { Card, Tabs, Select } from 'antd';
import { Print } from '@mui/icons-material';
import { IconButton, FormControlLabel, Switch } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { find, get, filter } from 'lodash';
import styled from 'styled-components';
import { useActivityStandardsView } from 'store/main_book_popup/selectors';
import { useStandards } from 'store/story/selectors';
import { useAllBook } from 'store/books/selectors';
import usePopUpAtOnce from './hooks/usePopUpAtOnce';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useReactToPrint } from 'react-to-print';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { useMobileDetect, useTabletDetection } from 'containers/chat/hooks';
import Loading from './Loading';
import COLOR from 'utils/constants/color';
import { filterStateStandardByEnabledStates, formatStateNameForDisplay, normalizeStateForComparison, statesMatch, getStateNameFromCode, getStateCodeFromName, formatGradeLevelForDisplay, getStateNameDisplayForCode, compareGradesForIllinoisCommonCore } from 'utils/lib/standardHelper';
import SentryWrap from './Sentry';

const Wrapper = styled.div`
    position: absolute;
    top: ${p => p.pathname?.includes('ebook') ? '40px' : '0px'};
    width: ${p => p.width}px !important;
    height: ${p => p.height}px !important;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 2;
    box-shadow: 7px -3px 26px 20px rgb(66 66 66), -4px -3px 26px 15px rgb(95 109 110);
        
    @media print {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100% !important;
        height: auto !important;
        max-height: none !important;
        background-color: white !important;
        box-shadow: none !important;
        z-index: 9999 !important;
        margin: 0 !important;
        padding: 0 !important;
    }
`;

const ContentWrapper = styled.div`
    background-color: ${COLOR.white};
    width: 100%;
    height: calc(100% - 48px);
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px;
       
    @media print {
        height: auto !important;
        max-height: none !important;
        overflow: visible !important;
        padding: 15mm 10mm !important;
        margin: 0 !important;
        display: block !important;
    }
`;

const PrintContent = styled.div`
    @media print {
        position: relative !important;
        width: 100% !important;
        height: auto !important;
        max-height: none !important;
        min-height: auto !important;
        background-color: white;
        padding: 0;
        overflow: visible !important;
        page-break-inside: auto;
        margin: 0 !important;
        display: block !important;
        visibility: visible !important;
        page-break-before: auto !important;
        
        * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            page-break-inside: auto;
        }
        
        @page {
            margin: 10mm 5mm;
            size: A4;
        }
        
        div {
            box-sizing: border-box !important;
        }
        
        > div:first-child {
            page-break-before: auto !important;
        }
        
        .activity-print-item:first-child {
            page-break-before: auto !important;
            margin-top: 0 !important;
        }
        
        .activity-print-item {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            orphans: 3 !important;
            widows: 3 !important;
        }
        
        .ant-tabs-content-holder {
            page-break-before: auto !important;
        }
        
        .ant-tabs-content {
            page-break-before: auto !important;
        }
    }
`;

const SelectActivityWrapper = styled.div`
    @media print {
        display: none !important;
    }
`;

const ActivityCard = styled(Card)`
    margin-bottom: 16px;
    background-color: #f0f7ff;
    border: 1px solid #91d5ff;
    
    @media print {
        margin-bottom: 20px !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        padding: 12px !important;
        page-break-before: auto !important;
        orphans: 3 !important;
        widows: 3 !important;
    }
`;

const ActivityName = styled.div`
    font-size: 16px;
    font-weight: 600;
    color:rgb(224 16 16);
    margin-bottom: 16px;
    
    @media print {
        font-size: 15px !important;
        margin-bottom: 12px !important;
        text-align: left !important;
    }
`;

const QuestionHeading = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #096dd9;
    margin-top: 16px;
    margin-bottom: 8px;
    
    &:first-of-type {
        margin-top: 0;
    }
    
    @media print {
        font-size: 13px !important;
        margin-top: 12px !important;
        margin-bottom: 6px !important;
    }
`;

const StandardCard = styled(Card)`
    margin-bottom: 12px;
    background-color: #ffffff;
    border: 1px solid #e0e0e0;
    
    @media print {
        margin-bottom: 10px !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        padding: 10px !important;
    }
`;

const StandardCode = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #1890ff;
    margin-bottom: 8px;
    
    @media print {
        font-size: 13px !important;
        margin-bottom: 6px !important;
        text-align: left !important;
    }
`;

const StateCodeLabel = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 8px;
    
    @media print {
        font-size: 12px !important;
        margin-bottom: 6px !important;
        text-align: left !important;
    }
`;

const GradeLevelLabel = styled.div`
    font-size: 13px;
    font-weight: 500;
    color: #6b7280;
    margin-bottom: 8px;
    
    @media print {
        font-size: 12px !important;
        margin-bottom: 6px !important;
        text-align: left !important;
    }
`;

const Description = styled.div`
    font-size: 13px;
    color: #374151;
    line-height: 1.5;
    white-space: pre-wrap;
    margin-top: 12px;
    
    @media print {
        font-size: 12px !important;
        line-height: 1.6 !important;
        margin-top: 8px !important;
        text-align: justify !important;
    }
`;

const TabContent = styled.div`
    padding: 16px 0;
    
    @media print {
        padding: 0 !important;
        page-break-inside: auto !important;
        orphans: 3 !important;
        widows: 3 !important;
    }
`;

const StyledTabs = styled(Tabs)`
    @media print {
        .ant-tabs-nav {
            margin-bottom: 15px !important;
            padding: 0 !important;
            page-break-after: avoid !important;
        }
        
        .ant-tabs-tab {
            padding: 8px 12px !important;
            font-size: 13px !important;
        }
        
        .ant-tabs-content-holder {
            padding: 0 !important;
            overflow: visible !important;
            height: auto !important;
            page-break-before: auto !important;
        }
        
        .ant-tabs-content {
            padding: 0 !important;
            height: auto !important;
            overflow: visible !important;
            page-break-before: auto !important;
        }
        
        .ant-tabs-tabpane,
        .ant-tabs-tabpane-active,
        .ant-tabs-tabpane-hidden {
            display: block !important;
            visibility: visible !important;
            height: auto !important;
            overflow: visible !important;
            opacity: 1 !important;
            page-break-before: auto !important;
        }
        
        .ant-tabs-tabpane > div:first-child {
            page-break-before: auto !important;
        }
    }
`;

const Header = styled.div`
    background-color: #91d5ff;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    padding: 0 20px;
    
    @media print {
        display: none !important;
    }
`;

const HeaderTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${COLOR.black200};
    text-align: center;
    flex: 1;
`;

const HeaderControls = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding-right: 60px;
`;

const StyledFormControlLabel = muiStyled(FormControlLabel)({
    margin: 0,
    '& .MuiFormControlLabel-label': {
        fontSize: '12px',
        color: COLOR.black200,
        fontWeight: 500,
    },
});

const StyledSwitch = muiStyled(Switch)({
    '&.MuiSwitch-sizeSmall': {
        width: '40px',
        height: '25px',
    },
    '& .Mui-checked': {
        transform: 'translateX(16px) !important',
    },
});

const Title = styled.div`
    font-size: 20px;
    font-weight: 600;
    color: ${COLOR.black200};
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid ${COLOR.black600};
    
    @media print {
        font-size: 18px !important;
        margin-bottom: 15px !important;
        padding-bottom: 8px !important;
        text-align: center !important;
    }
`;

export default function ActivityStandardsPopup({ getRef, onClose, customSize }) {
    const { activityStandardsView, revokeActivityStandardsView, invokeActivityStandardsView } = useActivityStandardsView();
    const globalStandards = useStandards();
    const booksAllList = useAllBook();
    const [activeStateCode, setActiveStateCode] = useState('');
    const [showUnitView, setShowUnitView] = useState(false);
    const [showEnglishDescription, setShowEnglishDescription] = useState(false);
    const [selectedActivityId, setSelectedActivityId] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [style, setStyle] = useState({ height: 0, width: 0 });
    const [printLoader, setPrintLoader] = useState(false);
    const { updatePopUpAtOnce } = usePopUpAtOnce();
    const location = useLocation();
    const search = queryString.parse(location.search);
    const currentUrlPage = search.page ? String(search.page) : null;
    const printRef = useRef();
    const prevPageRef = useRef(null);
    const showAlert = useAlert();
    const isMobile = useMobileDetect();
    const isTablet = useTabletDetection();
    
    const bookData = activityStandardsView.bookData;
    const units = activityStandardsView.units || [];
    const pages = activityStandardsView.pages || [];
    const currentPage = activityStandardsView.currentPage;
    
    const bookId = search.chapter || location.pathname.split('/').pop();
    const actualBook = useMemo(() => {
        return find(booksAllList, (book) => book.id === bookId);
    }, [booksAllList, bookId]);
    
    const ebookGradeLevels = actualBook?.grade_levels || [];
    const handleClose = () => {
        revokeActivityStandardsView();
        updatePopUpAtOnce(false);
        if (onClose) {
            onClose();
        }
    };

    useEffect(() => {
        handleClose();
        setSelectedActivityId(null);
        setActiveStateCode('');
        setIsInitialLoad(true);
    }, [location.pathname]);

    useEffect(() => {
        const isOpen = activityStandardsView.open;
        const pageToTrack = currentUrlPage || currentPage;
        const pageChanged = prevPageRef.current !== null && prevPageRef.current !== pageToTrack;
        
        if (!isOpen) {
            if (pageToTrack) {
                prevPageRef.current = pageToTrack;
            }
            return;
        }
        
        if (pageChanged && pageToTrack) {
            handleClose();
            setSelectedActivityId(null);
            setActiveStateCode('');
            setIsInitialLoad(true);
        }
        
        if (pageToTrack) {
            prevPageRef.current = pageToTrack;
        }
    }, [currentUrlPage, currentPage, activityStandardsView.open, location.search]);

    useEffect(() => {
        if (customSize && customSize.width && customSize.height) {
            setStyle({
                width: customSize.width,
                height: customSize.height
            });
            return;
        }

        const el = getRef()?.current || getRef();
        if (!el) return;
    
        const observer = new ResizeObserver(entries => {
            const rect = entries[0].contentRect;
            setStyle({
                width: rect.width,
                height: rect.height
            });
        });
    
        observer.observe(el);
    
        return () => observer.disconnect();
    }, [getRef, customSize]);

    const getPageMask = (pageId) => {
        if (!pages || !Array.isArray(pages)) return pageId;
        const page = find(pages, p => String(p.pageId) === String(pageId));
        return get(page, 'numberMasked', pageId);
    };

    const getCurrentUnit = useMemo(() => {
        if (!units || !Array.isArray(units) || !currentPage) return null;
        return units.find(unit => {
            const startPage = parseInt(unit.start);
            const finishPage = parseInt(unit.finish);
            const pageNum = parseInt(currentPage);
            return pageNum >= startPage && pageNum <= finishPage;
        });
    }, [units, currentPage]);

    const getFilteredActivities = useMemo(() => {
        if (!bookData) {
            return activityStandardsView.activities || [];
        }
        
        let filteredActivities = [];
        
        if (showUnitView && getCurrentUnit) {
            const startPage = parseInt(getCurrentUnit.start);
            const finishPage = parseInt(getCurrentUnit.finish);
            
            filteredActivities = filter(bookData.activities || [], (activity) => {
                const activityPage = parseInt(get(activity, 'page') || get(activity, 'data.page') || 0);
                return activityPage >= startPage && activityPage <= finishPage;
            });
            
            const unitClassActivities = filter(bookData.classActivities || [], (classActivity) => {
                const activityPage = parseInt(get(classActivity, 'data.page') || 0);
                return activityPage >= startPage && activityPage <= finishPage;
            });
            
            filteredActivities = [...filteredActivities, ...unitClassActivities];
        } else {
            filteredActivities = activityStandardsView.activities || [];
        }
        
        const uniqueActivities = [];
        const seenIds = new Set();
        
        filteredActivities.forEach(activity => {
            const dataId = get(activity, 'data.id');
            const activityId = get(activity, 'activityId');
            const wrapperId = get(activity, 'id');
            const finalId = dataId || activityId || wrapperId;
            
            if (finalId && !seenIds.has(finalId)) {
                seenIds.add(finalId);
                uniqueActivities.push(activity);
            }
        });
        
        return uniqueActivities;
    }, [bookData, showUnitView, getCurrentUnit, activityStandardsView.activities]);

    const getActivitiesWithStandards = useMemo(() => {
        const activities = getFilteredActivities;
        
        if (!Array.isArray(activities) || activities.length === 0) return [];
        
        const filtered = activities.filter(activity => {
            const questionsDirect = get(activity, 'questions', []);
            const questionsNested = get(activity, 'data.questions', []);
            const questions = Array.isArray(questionsDirect) && questionsDirect.length > 0 ? questionsDirect : (Array.isArray(questionsNested) ? questionsNested : []);
            return Array.isArray(questions) && questions.length > 0 && questions.some(q => 
                (q && q.state_standard && Array.isArray(q.state_standard) && q.state_standard.length > 0) || 
                (q && q.standard)
            );
        });
        
        return filtered.map(activity => {
            const questionsDirect = get(activity, 'questions', []);
            const questionsNested = get(activity, 'data.questions', []);
            const questions = Array.isArray(questionsDirect) && questionsDirect.length > 0 ? questionsDirect : (Array.isArray(questionsNested) ? questionsNested : []);
            const allStateStandards = [];
            const activityStandards = [];
            const standardsWithStateInfo = [];
            const questionsWithStandards = [];

            questions.forEach(question => {
                const questionAllStateStandards = [];
                const questionStandards = [];
                const questionStandardsWithStateInfo = [];

                if (question && question.state_standard && Array.isArray(question.state_standard) && question.state_standard.length > 0) {
                    const enabledStateCodes = activityStandardsView.enabledStateCodes || [];
                    const filteredStateStandard = filterStateStandardByEnabledStates(question.state_standard, enabledStateCodes);

                    filteredStateStandard.forEach((stateStd) => {
                        if (stateStd && Array.isArray(stateStd.standard) && stateStd.standard.length > 0) {
                            const stateName = stateStd.standard_name || '';
                            const stateEntry = {
                                state_name: stateName,
                                grade: stateStd.grade,
                                standard: [...stateStd.standard]
                            };
                            questionAllStateStandards.push(stateEntry);
                            allStateStandards.push(stateEntry);

                            stateStd.standard.forEach(abbr => {
                                if (!activityStandards.includes(abbr)) {
                                    activityStandards.push(abbr);
                                }
                                if (!questionStandards.includes(abbr)) {
                                    questionStandards.push(abbr);
                                }
                                if (!standardsWithStateInfo.find(s => s.abbr === abbr && s.state === stateName)) {
                                    standardsWithStateInfo.push({
                                        abbr: abbr,
                                        state: stateName
                                    });
                                }
                                if (!questionStandardsWithStateInfo.find(s => s.abbr === abbr && s.state === stateName)) {
                                    questionStandardsWithStateInfo.push({
                                        abbr: abbr,
                                        state: stateName
                                    });
                                }
                            });
                        }
                    });
                } else if (question && question.standard) {
                    const standardAbbrs = typeof question.standard === 'string' && question.standard.includes(',')
                        ? question.standard.split(',').map(s => s.trim()).filter(s => s)
                        : [question.standard];

                    standardAbbrs.forEach(abbr => {
                        if (!activityStandards.includes(abbr)) {
                            activityStandards.push(abbr);
                        }
                        if (!questionStandards.includes(abbr)) {
                            questionStandards.push(abbr);
                        }
                        if (!standardsWithStateInfo.find(s => s.abbr === abbr)) {
                            standardsWithStateInfo.push({
                                abbr: abbr,
                                state: ''
                            });
                        }
                        if (!questionStandardsWithStateInfo.find(s => s.abbr === abbr)) {
                            questionStandardsWithStateInfo.push({
                                abbr: abbr,
                                state: ''
                            });
                        }
                    });
                }

                if (questionAllStateStandards.length > 0 || questionStandards.length > 0) {
                    questionsWithStandards.push({
                        allStateStandards: questionAllStateStandards,
                        standards: questionStandards,
                        standardsWithStateInfo: questionStandardsWithStateInfo
                    });
                }
            });

            const activityPage = get(activity, 'page') || get(activity, 'data.page');
            const mask = getPageMask(activityPage);
            const activityName = get(activity, 'displayName') || get(activity, 'data.displayName') || get(activity, 'title') || get(activity, 'data.name') || get(activity, 'data.title') || 'Untitled Activity';
            const displayName = mask && mask !== String(activityPage) ? `${activityName} (${mask})` : activityName;

            return {
                activity,
                activityName: displayName,
                standards: activityStandards,
                standardsWithStateInfo: standardsWithStateInfo,
                allStateStandards: allStateStandards,
                questions: questionsWithStandards
            };
        });
    }, [getFilteredActivities, getPageMask]);

    const getStandardData = (standardAbbrs, standardsWithStateInfo = []) => {
        const standards = (activityStandardsView.standards && activityStandardsView.standards.length > 0) 
            ? activityStandardsView.standards 
            : globalStandards;
        const enabledStateCodes = activityStandardsView.enabledStateCodes || [];
        
        return standardAbbrs.map((standardAbbr) => {
            const selectedStandard = find(standards, ["abbr", standardAbbr]);
            const stateInfo = standardsWithStateInfo.find(s => s.abbr === standardAbbr);
            const stateFromData = stateInfo ? stateInfo.state : '';
            
            if (!selectedStandard) {
                return {
                    standard: { abbr: standardAbbr, description: '', description_english: '' },
                    description: '',
                    descriptionEnglish: '',
                    state: stateFromData
                };
            }

            const standardState = get(selectedStandard, 'state', '') || stateFromData;

            return {
                standard: selectedStandard,
                description: get(selectedStandard, 'description', ''),
                descriptionEnglish: get(selectedStandard, 'description_english', ''),
                state: standardState
            };
        });
    };

    const getAllEnabledStates = useMemo(() => {
        const allStates = new Map();
        const enabledStateCodes = activityStandardsView.enabledStateCodes || [];
        const enabledStateNames = enabledStateCodes.map(code => normalizeStateForComparison(code)).filter(Boolean);
        
        let activitiesToProcess = getActivitiesWithStandards;
        
        if (selectedActivityId && selectedActivityId !== 'all') {
            activitiesToProcess = activitiesToProcess.filter(activityData => {
                const activityId = get(activityData.activity, 'data.id') || get(activityData.activity, 'id') || get(activityData.activity, 'activityId');
                return String(activityId) === String(selectedActivityId);
            });
        }
        
        activitiesToProcess.forEach(activityData => {
            const allStateStandards = activityData.allStateStandards || [];
            
            if (allStateStandards.length > 0) {
                allStateStandards.forEach(stateStd => {
                    const stateCode = stateStd.state_name || '';
                    const stateName = normalizeStateForComparison(stateCode);
                    const hasStandards = stateStd.standard && Array.isArray(stateStd.standard) && stateStd.standard.length > 0;
                    
                    if (!hasStandards) return;
                    
                    if (enabledStateNames.length === 0) return;
                    
                    const isEnabled = enabledStateNames.some(enabledName => statesMatch(stateCode, enabledName));
                    
                    if (!isEnabled) return;
                    
                    if (Array.isArray(ebookGradeLevels) && ebookGradeLevels.length > 0) {
                        const matchingGradeLevel = ebookGradeLevels.find(gl => {
                            const glState = get(gl, 'state', '');
                            return statesMatch(glState, stateCode);
                        });
                        
                        if (!matchingGradeLevel) return;
                        
                        const ebookGradeLevel = get(matchingGradeLevel, 'grade_level', '');
                        const stateGrade = stateStd.grade || null;
                        
                        if (ebookGradeLevel && stateGrade) {
                            const ebookGrades = String(ebookGradeLevel).split(',').map(g => g.trim()).filter(g => g);
                            const stateGradeStr = String(stateGrade);
                            const stateGrades = stateGradeStr.split(',').map(g => g.trim()).filter(g => g);
                            const standardState = String(stateCode).toUpperCase();
                            
                            const gradeMatches = ebookGrades.some(ebookGrade => 
                                stateGrades.some(stateGrade => compareGradesForIllinoisCommonCore(ebookGrade, stateGrade, standardState))
                            );
                            
                            if (!gradeMatches) return;
                        }
                    }
                    
                    const displayStateName = getStateNameFromCode(stateCode) || stateName;
                    const stateCodeForDisplay = getStateCodeFromName(displayStateName) || stateCode;
                    if (!allStates.has(stateName)) {
                        allStates.set(stateName, {
                            code: stateCodeForDisplay,
                            state: displayStateName
                        });
                    }
                });
            } else {
                const standardData = getStandardData(activityData.standards, activityData.standardsWithStateInfo || []);
                standardData.forEach(data => {
                    if (!data.standard || !data.standard.abbr) return;
                    
                    const stateCode = data.state || '';
                    const stateNameNormalized = normalizeStateForComparison(stateCode);
                    const stateNameDisplay = getStateNameFromCode(stateCode) || stateNameNormalized;
                    const stateCodeForDisplay = getStateCodeFromName(stateNameDisplay) || stateCode;
                    
                    if (enabledStateNames.length === 0) return;
                    
                    const isEnabled = enabledStateNames.some(enabledName => statesMatch(stateCode, enabledName));
                    
                    if (!isEnabled) return;
                    
                    if (Array.isArray(ebookGradeLevels) && ebookGradeLevels.length > 0) {
                        const matchingGradeLevel = ebookGradeLevels.find(gl => {
                            const glState = get(gl, 'state', '');
                            return statesMatch(glState, stateCode);
                        });
                        
                        if (!matchingGradeLevel) return;
                        
                        const ebookGradeLevel = get(matchingGradeLevel, 'grade_level', '');
                        const standardGradeLevel = get(data.standard, 'grade_level', '');
                        
                        if (ebookGradeLevel && standardGradeLevel) {
                            const ebookGrades = String(ebookGradeLevel).split(',').map(g => g.trim()).filter(g => g);
                            const standardGrades = String(standardGradeLevel).split(',').map(g => g.trim()).filter(g => g);
                            const standardState = String(stateCode).toUpperCase();
                            
                            const gradeMatches = ebookGrades.some(ebookGrade => 
                                standardGrades.some(standardGrade => compareGradesForIllinoisCommonCore(ebookGrade, standardGrade, standardState))
                            );
                            
                            if (!gradeMatches) return;
                        }
                    }
                    
                    if (!allStates.has(stateNameNormalized)) {
                        allStates.set(stateNameNormalized, {
                            code: stateCodeForDisplay,
                            state: stateNameDisplay
                        });
                    }
                });
            }
        });
        
        return Array.from(allStates.values());
    }, [getActivitiesWithStandards, selectedActivityId, ebookGradeLevels]);

    const getUniqueActivitiesList = useMemo(() => {
        const uniqueActivitiesMap = new Map();
        getActivitiesWithStandards.forEach((activityData) => {
            const activityId = get(activityData.activity, 'data.id') || get(activityData.activity, 'id') || get(activityData.activity, 'activityId');
            if (activityId && !uniqueActivitiesMap.has(activityId)) {
                uniqueActivitiesMap.set(activityId, activityData);
            }
        });
        return Array.from(uniqueActivitiesMap.values());
    }, [getActivitiesWithStandards]);

    const hasEnabledStates = getAllEnabledStates.length > 0;
    const selectedStateCode = activeStateCode || (hasEnabledStates ? getAllEnabledStates[0].code : '');

    const getFilteredActivitiesForState = useMemo(() => {
        const enabledStateCodes = activityStandardsView.enabledStateCodes || [];
        const enabledStateNames = enabledStateCodes.map(code => normalizeStateForComparison(code)).filter(Boolean);
        
        return getUniqueActivitiesList.filter(activityData => {
            const allStateStandards = activityData.allStateStandards || [];
            
            if (allStateStandards.length > 0) {
                return allStateStandards.some(stateStd => {
                    if (!stateStd.standard || !Array.isArray(stateStd.standard) || stateStd.standard.length === 0) return false;
                    const stateCode = stateStd.state_name || stateStd.standard_name || '';
                    const stateCodeUpper = String(stateCode).toUpperCase().trim();
                    const isCommonCoreState = stateCodeUpper === 'COMMONCORE' || stateCodeUpper === 'COMMON CORE';
                    
                    if (enabledStateNames.length === 0) return true;
                    
                    const stateMatches = enabledStateNames.some(enabledName => {
                        if (isCommonCoreState) {
                            const enabledNameUpper = String(enabledName).toUpperCase().trim();
                            const isEnabledCommonCore = enabledNameUpper === 'COMMONCORE' || enabledNameUpper === 'COMMON CORE';
                            if (isEnabledCommonCore) return true;
                        }
                        return statesMatch(stateCode, enabledName) || 
                               normalizeStateForComparison(stateCode) === normalizeStateForComparison(enabledName);
                    });
                    
                    if (!stateMatches) return false;
                    
                    if (Array.isArray(ebookGradeLevels) && ebookGradeLevels.length > 0) {
                        const matchingGradeLevel = ebookGradeLevels.find(gl => {
                            const glState = get(gl, 'state', '');
                            return statesMatch(glState, stateCode);
                        });
                        
                        if (!matchingGradeLevel) return false;
                        
                        const ebookGradeLevel = get(matchingGradeLevel, 'grade_level', '');
                        const stateGrade = stateStd.grade || null;
                        
                        if (ebookGradeLevel && stateGrade) {
                            const ebookGrades = String(ebookGradeLevel).split(',').map(g => g.trim()).filter(g => g);
                            const stateGradeStr = String(stateGrade);
                            const stateGrades = stateGradeStr.split(',').map(g => g.trim()).filter(g => g);
                            const standardState = String(stateCode).toUpperCase();
                            
                            const gradeMatches = ebookGrades.some(ebookGrade => 
                                stateGrades.some(stateGrade => compareGradesForIllinoisCommonCore(ebookGrade, stateGrade, standardState))
                            );
                            
                            if (!gradeMatches) return false;
                        }
                    }
                    
                    return true;
                });
            } else {
                const standards = (activityStandardsView.standards && activityStandardsView.standards.length > 0) 
                    ? activityStandardsView.standards 
                    : globalStandards;
                
                const standardData = getStandardData(activityData.standards || [], activityData.standardsWithStateInfo || []);
                return standardData.some(data => {
                    if (!data.standard || !data.standard.abbr) return false;
                    const stateCode = data.state || '';
                    
                    if (enabledStateNames.length === 0) return true;
                    
                    const stateCodeUpper = String(stateCode).toUpperCase().trim();
                    const isCommonCoreState = stateCodeUpper === 'COMMONCORE' || stateCodeUpper === 'COMMON CORE';
                    
                    const stateMatches = enabledStateNames.some(enabledName => {
                        if (isCommonCoreState) {
                            const enabledNameUpper = String(enabledName).toUpperCase().trim();
                            const isEnabledCommonCore = enabledNameUpper === 'COMMONCORE' || enabledNameUpper === 'COMMON CORE';
                            if (isEnabledCommonCore) return true;
                        }
                        return statesMatch(stateCode, enabledName) || 
                               normalizeStateForComparison(stateCode) === normalizeStateForComparison(enabledName);
                    });
                    
                    if (!stateMatches) return false;
                    
                    if (Array.isArray(ebookGradeLevels) && ebookGradeLevels.length > 0) {
                        const matchingGradeLevel = ebookGradeLevels.find(gl => {
                            const glState = get(gl, 'state', '');
                            return statesMatch(glState, stateCode);
                        });
                        
                        if (!matchingGradeLevel) return false;
                        
                        const ebookGradeLevel = get(matchingGradeLevel, 'grade_level', '');
                        const standardGradeLevel = get(data.standard, 'grade_level', '');
                        
                        if (ebookGradeLevel && standardGradeLevel) {
                            const ebookGrades = String(ebookGradeLevel).split(',').map(g => g.trim()).filter(g => g);
                            const standardGrades = String(standardGradeLevel).split(',').map(g => g.trim()).filter(g => g);
                            const standardState = String(stateCode).toUpperCase();
                            
                            const gradeMatches = ebookGrades.some(ebookGrade => 
                                standardGrades.some(standardGrade => compareGradesForIllinoisCommonCore(ebookGrade, standardGrade, standardState))
                            );
                            
                            if (!gradeMatches) return false;
                        }
                    }
                    
                    return true;
                });
            }
        });
    }, [getUniqueActivitiesList, activityStandardsView.enabledStateCodes, activityStandardsView.standards, globalStandards, ebookGradeLevels]);
    
    const getStatesForActivity = useMemo(() => {
        return (activityData) => {
            const states = [];
            const allStateStandards = activityData.allStateStandards || [];
            const enabledStateCodes = activityStandardsView.enabledStateCodes || [];
            const enabledStateNames = enabledStateCodes.map(code => normalizeStateForComparison(code)).filter(Boolean);
            
            if (allStateStandards.length > 0) {
                allStateStandards.forEach(stateStd => {
                    if (!stateStd.standard || !Array.isArray(stateStd.standard) || stateStd.standard.length === 0) return;
                    const stateCode = stateStd.state_name || stateStd.standard_name || '';
                    const stateName = normalizeStateForComparison(stateCode);
                    const stateCodeUpper = String(stateCode).toUpperCase().trim();
                    const isCommonCoreState = stateCodeUpper === 'COMMONCORE' || stateCodeUpper === 'COMMON CORE';
                    
                    const isEnabled = enabledStateNames.some(enabledName => {
                        if (isCommonCoreState) {
                            const enabledNameUpper = String(enabledName).toUpperCase().trim();
                            const isEnabledCommonCore = enabledNameUpper === 'COMMONCORE' || enabledNameUpper === 'COMMON CORE';
                            if (isEnabledCommonCore) return true;
                        }
                        return statesMatch(stateCode, enabledName) || 
                               normalizeStateForComparison(stateCode) === normalizeStateForComparison(enabledName);
                    });
                    
                    if (isEnabled) {
                        if (Array.isArray(ebookGradeLevels) && ebookGradeLevels.length > 0) {
                            const matchingGradeLevel = ebookGradeLevels.find(gl => {
                                const glState = get(gl, 'state', '');
                                return statesMatch(glState, stateCode);
                            });
                            
                            if (matchingGradeLevel) {
                                const ebookGradeLevel = get(matchingGradeLevel, 'grade_level', '');
                                const stateGrade = stateStd.grade || null;
                                
                                if (ebookGradeLevel && stateGrade) {
                                    const ebookGrades = String(ebookGradeLevel).split(',').map(g => g.trim()).filter(g => g);
                                    const stateGradeStr = String(stateGrade);
                                    const stateGrades = stateGradeStr.split(',').map(g => g.trim()).filter(g => g);
                                    const standardState = String(stateCode).toUpperCase();
                                    
                                    const gradeMatches = ebookGrades.some(ebookGrade => 
                                        stateGrades.some(stateGrade => compareGradesForIllinoisCommonCore(ebookGrade, stateGrade, standardState))
                                    );
                                    
                                    if (!gradeMatches) return;
                                }
                            } else {
                                return;
                            }
                        }
                        
                        let displayStateName = getStateNameDisplayForCode(stateCode);
                        if (!displayStateName && isCommonCoreState) {
                            displayStateName = 'Common Core';
                        }
                        if (!displayStateName) {
                            displayStateName = getStateNameFromCode(stateCode) || stateCode;
                        }
                        const stateCodeForDisplay = getStateCodeFromName(displayStateName) || getStateCodeFromName(stateCode) || (isCommonCoreState ? 'COMMONCORE' : stateCode);
                        
                        const stateInfo = getAllEnabledStates.find(s => 
                            normalizeStateForComparison(s.code) === stateName || 
                            normalizeStateForComparison(s.state) === normalizeStateForComparison(displayStateName)
                        );
                        
                        if (stateInfo && !states.find(s => s.code === stateInfo.code)) {
                            states.push(stateInfo);
                        }
                    }
                });
            } else {
                const standardData = getStandardData(activityData.standards, activityData.standardsWithStateInfo || []);
                standardData.forEach(data => {
                    if (!data.standard || !data.standard.abbr) return;
                    const stateCode = data.state || '';
                    const stateNameNormalized = normalizeStateForComparison(stateCode);
                    const stateCodeUpper = String(stateCode).toUpperCase().trim();
                    const isCommonCoreState = stateCodeUpper === 'COMMONCORE' || stateCodeUpper === 'COMMON CORE';
                    
                    const isEnabled = enabledStateNames.some(enabledName => {
                        if (isCommonCoreState) {
                            const enabledNameUpper = String(enabledName).toUpperCase().trim();
                            const isEnabledCommonCore = enabledNameUpper === 'COMMONCORE' || enabledNameUpper === 'COMMON CORE';
                            if (isEnabledCommonCore) return true;
                        }
                        return statesMatch(stateCode, enabledName) || 
                               normalizeStateForComparison(stateCode) === normalizeStateForComparison(enabledName);
                    });
                    
                    if (isEnabled) {
                        if (Array.isArray(ebookGradeLevels) && ebookGradeLevels.length > 0) {
                            const matchingGradeLevel = ebookGradeLevels.find(gl => {
                                const glState = get(gl, 'state', '');
                                return statesMatch(glState, stateCode);
                            });
                            
                            if (matchingGradeLevel) {
                                const ebookGradeLevel = get(matchingGradeLevel, 'grade_level', '');
                                const standardGradeLevel = get(data.standard, 'grade_level', '');
                                
                                if (ebookGradeLevel && standardGradeLevel) {
                                    const ebookGrades = String(ebookGradeLevel).split(',').map(g => g.trim()).filter(g => g);
                                    const standardGrades = String(standardGradeLevel).split(',').map(g => g.trim()).filter(g => g);
                                    const standardState = String(stateCode).toUpperCase();
                                    
                                    const gradeMatches = ebookGrades.some(ebookGrade => 
                                        standardGrades.some(standardGrade => compareGradesForIllinoisCommonCore(ebookGrade, standardGrade, standardState))
                                    );
                                    
                                    if (!gradeMatches) return;
                                }
                            } else {
                                return;
                            }
                        }
                        
                        const stateInfo = getAllEnabledStates.find(s => 
                            normalizeStateForComparison(s.code) === stateNameNormalized
                        );
                        
                        if (stateInfo && !states.find(s => s.code === stateInfo.code)) {
                            states.push(stateInfo);
                        }
                    }
                });
            }
            
            return states;
        };
    }, [getAllEnabledStates, activityStandardsView.enabledStateCodes, activityStandardsView.standards, globalStandards, ebookGradeLevels]);

    useEffect(() => {
        const filteredActivities = getFilteredActivitiesForState;
        
        if (isInitialLoad && filteredActivities.length > 0) {
            if (!selectedActivityId || selectedActivityId === 'all' || selectedActivityId === null) {
                const firstActivityId = get(filteredActivities[0].activity, 'data.id') || get(filteredActivities[0].activity, 'id') || get(filteredActivities[0].activity, 'activityId');
                if (firstActivityId) {
                    setSelectedActivityId(String(firstActivityId));
                }
            }
            setIsInitialLoad(false);
        } else if (!isInitialLoad && selectedActivityId && selectedActivityId !== 'all') {
            const currentActivity = getFilteredActivitiesForState.find(activityData => {
                const activityId = get(activityData.activity, 'data.id') || get(activityData.activity, 'id') || get(activityData.activity, 'activityId');
                return String(activityId) === String(selectedActivityId);
            });
            if (!currentActivity && getFilteredActivitiesForState.length > 0) {
                const firstActivityId = get(getFilteredActivitiesForState[0].activity, 'data.id') || get(getFilteredActivitiesForState[0].activity, 'id') || get(getFilteredActivitiesForState[0].activity, 'activityId');
                if (firstActivityId) {
                    setSelectedActivityId(String(firstActivityId));
                }
            } else if (!currentActivity) {
                setSelectedActivityId('all');
            }
        }
    }, [getFilteredActivitiesForState, isInitialLoad]);

    const renderStandardsForActivity = (activityData, stateCode) => {
        const standards = (activityStandardsView.standards && activityStandardsView.standards.length > 0) 
            ? activityStandardsView.standards 
            : globalStandards;
        const enabledStateCodes = activityStandardsView.enabledStateCodes || [];
        const allStateStandards = activityData.allStateStandards || [];
        
        if (allStateStandards.length > 0) {
            const filteredStateStandards = stateCode
                ? allStateStandards.filter(stateStd => {
                    if (stateCode === '') {
                        return !stateStd.state_name || stateStd.state_name === '';
                    }
                    const stateStdName = stateStd.state_name || '';
                    return statesMatch(stateStdName, stateCode);
                })
                : allStateStandards;
            
            const allStandardsToDisplay = [];
            const seenStandards = new Set();
            
            filteredStateStandards.forEach((stateStd) => {
                if (stateStd && Array.isArray(stateStd.standard) && stateStd.standard.length > 0) {
                    stateStd.standard.forEach(abbr => {
                        const standardObj = find(standards, ["abbr", abbr]);
                        const stateName = stateStd.state_name || '';
                        const grade = stateStd.grade || null;
                        
                        const uniqueKey = `${abbr}-${stateName}-${grade || ''}`;
                        
                        if (!seenStandards.has(uniqueKey)) {
                            seenStandards.add(uniqueKey);
                            allStandardsToDisplay.push({
                                abbr: abbr,
                                standard: standardObj || { abbr: abbr, description: '', description_english: '' },
                                description: standardObj ? get(standardObj, 'description', '') : '',
                                descriptionEnglish: standardObj ? get(standardObj, 'description_english', '') : '',
                                state: stateName,
                                grade: grade
                            });
                        }
                    });
                }
            });
            
            return allStandardsToDisplay.map((data, idx) => {
                const hasEnglishDescription = data.descriptionEnglish && data.descriptionEnglish.trim().length > 0;
                
                const displayDescription = showEnglishDescription 
                    ? (hasEnglishDescription ? data.descriptionEnglish : 'ENGLISH TRANSLATION IS NOT AVAILABLE.')
                    : (data.description || 'No description available');

                const displayAbbr = data.abbr;

                return (
                    <div key={`${data.state}-${data.abbr}-${idx}`} style={{
                        marginBottom: idx < allStandardsToDisplay.length - 1 ? '12px' : '0'
                    }}>
                        <StandardCard>
                            <StandardCode>{displayAbbr}</StandardCode>
                            {data.state && (
                                <StateCodeLabel>State: {getStateNameDisplayForCode(data.state)}</StateCodeLabel>
                            )}
                            {data.grade && (
                                <GradeLevelLabel>Grade Level: {formatGradeLevelForDisplay(data.grade, data.state)}</GradeLevelLabel>
                            )}
                            <Description>
                                {displayDescription}
                            </Description>
                        </StandardCard>
                    </div>
                );
            });
        }
        
        const standardData = getStandardData(activityData.standards, activityData.standardsWithStateInfo || []);
        const filteredStandardData = stateCode
            ? standardData.filter(data => {
                if (stateCode === '') {
                    return !data.state || data.state === '';
                }
                const dataState = data.state || '';
                return statesMatch(dataState, stateCode);
            })
            : standardData;
        
        const seenStandards = new Set();
        const uniqueStandardData = filteredStandardData.filter(data => {
            const uniqueKey = `${data.standard.abbr}-${data.state || ''}`;
            if (seenStandards.has(uniqueKey)) {
                return false;
            }
            seenStandards.add(uniqueKey);
            return true;
        });
        
        return uniqueStandardData.map((data, idx) => {
            const hasEnglishDescription = data.descriptionEnglish && data.descriptionEnglish.trim().length > 0;
            
            const displayDescription = showEnglishDescription 
                ? (hasEnglishDescription ? data.descriptionEnglish : 'ENGLISH TRANSLATION IS NOT AVAILABLE.')
                : (data.description || 'No description available');

            const displayAbbr = data.standard.abbr;

            return (
                <div key={idx} className="printable" style={{
                    marginBottom: idx < uniqueStandardData.length - 1 ? '12px' : '0'
                }}>
                    <StandardCard>
                        <StandardCode>{displayAbbr}</StandardCode>
                        {stateCode && (
                            <StateCodeLabel>State: {formatStateNameForDisplay(stateCode)}</StateCodeLabel>
                        )}
                        <Description>
                            {displayDescription}
                        </Description>
                    </StandardCard>
                </div>
            );
        });
    };

    const renderActivitiesForState = (stateCode) => {
        let activitiesList = getUniqueActivitiesList;
        
        if (selectedActivityId && selectedActivityId !== 'all') {
            activitiesList = activitiesList.filter(activityData => {
                const activityId = get(activityData.activity, 'data.id') || get(activityData.activity, 'id') || get(activityData.activity, 'activityId');
                return String(activityId) === String(selectedActivityId);
            });
        }

        return activitiesList.map((activityData, activityIdx) => {
            const allStateStandards = activityData.allStateStandards || [];

            if (stateCode) {
                let hasStandardsForState = false;
                if (allStateStandards.length > 0) {
                    hasStandardsForState = allStateStandards.some(stateStd => {
                        if (stateCode === '') {
                            return !stateStd.state_name || stateStd.state_name === '';
                        }
                        const stateStdName = stateStd.state_name || '';
                        return statesMatch(stateStdName, stateCode);
                    });
                } else {
                    const standardData = getStandardData(activityData.standards, activityData.standardsWithStateInfo || []);
                    hasStandardsForState = standardData.some(data => {
                        if (stateCode === '') {
                            return !data.state || data.state === '';
                        }
                        const dataState = data.state || '';
                        return statesMatch(dataState, stateCode);
                    });
                }
                if (!hasStandardsForState) return null;
            }

            const questions = activityData.questions || [];
            const hasQuestions = questions.length > 0;

            if (hasQuestions) {
                const questionSections = questions.map((questionData, qIdx) => {
                    const renderedStandards = renderStandardsForActivity(questionData, stateCode);
                    if (!renderedStandards || renderedStandards.length === 0) return null;
                    return (
                        <div key={qIdx} style={{ marginBottom: qIdx < questions.length - 1 ? '16px' : '0' }}>
                            <QuestionHeading>Question #{qIdx + 1}:</QuestionHeading>
                            {renderedStandards}
                        </div>
                    );
                }).filter(Boolean);
                if (questionSections.length === 0) return null;
                return (
                    <div key={activityIdx} style={{
                        marginBottom: activityIdx < activitiesList.length - 1 ? '24px' : '0',
                        pageBreakBefore: activityIdx === 0 ? 'auto' : 'auto'
                    }} className="activity-print-item">
                        <ActivityCard>
                            <TabContent>
                                <ActivityName>{activityData.activityName}</ActivityName>
                                {questionSections}
                            </TabContent>
                        </ActivityCard>
                    </div>
                );
            }

            const renderedStandards = renderStandardsForActivity(activityData, stateCode);
            if (!renderedStandards || renderedStandards.length === 0) return null;
            return (
                <div key={activityIdx} className="printable activity-print-item" style={{
                    marginBottom: activityIdx < activitiesList.length - 1 ? '24px' : '0',
                    pageBreakBefore: activityIdx === 0 ? 'auto' : 'auto'
                }}>
                    <ActivityCard>
                        <TabContent>
                            <ActivityName>{activityData.activityName}</ActivityName>
                            {renderedStandards}
                        </TabContent>
                    </ActivityCard>
                </div>
            );
        }).filter(Boolean);
    };
const printCall = useReactToPrint({
        contentRef: printRef,
        onBeforeGetContent: async () => {
            setPrintLoader(true);
        },
        onBeforePrint: async () => {
            setPrintLoader(false);
        },
        onAfterPrint: async () => {
            setPrintLoader(false);
        },
    });

    const handlePrint = () => {
        if (isMobile || isTablet) {
            showAlert("Print is not supported on this device!", "error");
            return;
        }
        printCall();
    };

    if (!activityStandardsView.open || getActivitiesWithStandards.length === 0) return null;

    const standards = activityStandardsView.standards && activityStandardsView.standards.length > 0 
        ? activityStandardsView.standards 
        : globalStandards;
    const areStandardsLoaded = Array.isArray(globalStandards) && globalStandards.length > 0;

    const class_name = 'main-book-popup printScreenPage videoPrintScreenCstm activityStandardsPopup';

    return (
    <SentryWrap>
        <Wrapper pathname={location.pathname} width={style.width} height={style.height + 36} className={class_name}>
            <CloseOutlined onClick={handleClose} style={{ 
                position: 'absolute', 
                top: '-10px', 
                right: '-10px', 
                cursor: 'pointer', 
                fontSize: '16px', 
                color: 'white', 
                background: 'red', 
                padding: '5px', 
                borderRadius: '50%',
                zIndex: 10
            }} />
            <IconButton
                onClick={handlePrint}
                aria-label="print"
                disabled={printLoader}
                style={{
                    position: 'absolute',
                    top: '-13px',
                    right: '20px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: 'white',
                    background: COLOR.green100,
                    padding: '5px',
                    borderRadius: '50%',
                    zIndex: 10
                }}
            >
                {printLoader ? <Loading size={20} white /> : <Print style={{ color: 'white' }} />}
            </IconButton>
            <Header>
                <div style={{ width: '120px' }}></div>
                <HeaderTitle>Activity Standards</HeaderTitle>
                <HeaderControls>
                    <StyledFormControlLabel
                        control={
                            <StyledSwitch
                                checked={showEnglishDescription}
                                onChange={(e) => {
                                    setShowEnglishDescription(e.target.checked);
                                }}
                                name="englishDescription"
                                color="primary"
                                size="small"
                            />
                        }
                        label="English"
                        style={{ marginRight: getCurrentUnit ? '16px' : '0' }}
                    />
                    {getCurrentUnit && (
                        <StyledFormControlLabel
                            control={
                                <StyledSwitch
                                    checked={showUnitView}
                                    onChange={(e) => {
                                        setShowUnitView(e.target.checked);
                                        setActiveStateCode(null);
                                        setSelectedActivityId('all');
                                    }}
                                    name="unitView"
                                    color="primary"
                                    size="small"
                                />
                            }
                            label="Unit"
                        />
                    )}
                </HeaderControls>
            </Header>
            <ContentWrapper>
            <div ref={printRef} className="printable activity-standards-print-root" style={{ width: '100%' }}>
                <PrintContent className="printable">
                {getFilteredActivitiesForState.length > 0 && (
                    <SelectActivityWrapper style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <label style={{ fontSize: '14px', fontWeight: 500, color: COLOR.black200 }}>Select Activity:</label>
                        <Select
                            value={selectedActivityId || undefined}
                            onChange={(value) => {
                                setIsInitialLoad(false);
                                setSelectedActivityId(value);
                                
                                if (value && value !== 'all' && hasEnabledStates && getAllEnabledStates.length > 0) {
                                    const selectedActivity = getFilteredActivitiesForState.find(activityData => {
                                        const activityId = get(activityData.activity, 'data.id') || get(activityData.activity, 'id') || get(activityData.activity, 'activityId');
                                        return String(activityId) === String(value);
                                    });
                                    
                                    if (selectedActivity) {
                                        const statesForActivity = getStatesForActivity(selectedActivity);
                                        const currentStateCode = activeStateCode || selectedStateCode;
                                        
                                        const currentStateExists = statesForActivity.some(state => 
                                            statesMatch(state.code, currentStateCode) || 
                                            normalizeStateForComparison(state.code) === normalizeStateForComparison(currentStateCode)
                                        );
                                        
                                        if (!currentStateExists && statesForActivity.length > 0) {
                                            setActiveStateCode(statesForActivity[0].code);
                                        }
                                    }
                                }
                            }}
                            style={{ width: 300, minWidth: 200 }}
                            size="large"
                        >
                            {getFilteredActivitiesForState.map((activityData) => {
                                const activityId = get(activityData.activity, 'data.id') || get(activityData.activity, 'id') || get(activityData.activity, 'activityId');
                                return (
                                    <Select.Option key={activityId} value={String(activityId)}>
                                        {activityData.activityName}
                                    </Select.Option>
                                );
                            })}
                            <Select.Option value="all">Show All</Select.Option>
                        </Select>
                    </SelectActivityWrapper>
                )}
                {!areStandardsLoaded ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                        <Loading size={40} />
                        <div style={{ marginTop: '16px', color: '#666' }}>Loading standards...</div>
                    </div>
                ) : !hasEnabledStates ? (
                    <div>
                        {renderActivitiesForState(null)}
                    </div>
                ) : (
                    <StyledTabs
                        defaultActiveKey={selectedStateCode}
                        activeKey={selectedStateCode}
                        items={getAllEnabledStates.map((stateInfo) => ({
                            key: stateInfo.code,
                            label: `${getStateNameDisplayForCode(stateInfo.state)}`,
                            children: (
                                <div>
                                    {renderActivitiesForState(stateInfo.code)}
                                </div>
                            )
                        }))}
                        onChange={(key) => setActiveStateCode(key)}
                    />
                )}
                </PrintContent>
            </div>
            </ContentWrapper>
        </Wrapper>
    </SentryWrap>
    );
}
